import SectionLabel from '../common/SectionLabel';
import { formatCurrency, formatPercent } from '../../utils/formatting';
import { calcScenarioMonthly } from '../../utils/calculations';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function ImpactPanel({ monthlyImpact, profitFloorPct, onProfitFloorPctChange, baseMonthlyNOIArray, baseMonthlyIncomeArray, baseAnnualRevenue, scenarios }) {
  // Annual totals from scenarios
  const totalAdditionalRevenue = monthlyImpact.reduce((sum, m) => sum + Math.max(m.revenue, 0), 0);
  const totalAdditionalCost = monthlyImpact.reduce((sum, m) => sum + Math.min(m.cost, 0), 0);
  const netAnnualImpact = monthlyImpact.reduce((sum, m) => sum + m.net, 0);

  // Annual P&L totals from real data
  const baseAnnualNOI = (baseMonthlyNOIArray || []).reduce((s, v) => s + v, 0);
  const baseAnnualIncome = (baseMonthlyIncomeArray || []).reduce((s, v) => s + v, 0);

  // Annual net profit % BEFORE scenarios
  const baseNetProfitPct = baseAnnualIncome > 0 ? baseAnnualNOI / baseAnnualIncome : 0;

  // Annual net profit % AFTER scenarios
  const adjustedAnnualNOI = baseAnnualNOI + netAnnualImpact;
  const adjustedAnnualIncome = baseAnnualIncome + totalAdditionalRevenue;
  const adjustedNetProfitPct = adjustedAnnualIncome > 0 ? adjustedAnnualNOI / adjustedAnnualIncome : 0;

  // Floor check on ANNUAL net profit %
  const floorPct = profitFloorPct / 100;
  const annualBelowFloor = adjustedNetProfitPct < floorPct;

  return (
    <div className="space-y-4">
      {/* Profit Floor + Annual Net Profit % */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-navy block">
              Annual Net Profit Floor
            </span>
          </div>
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={profitFloorPct}
              onChange={(e) => onProfitFloorPctChange(parseFloat(e.target.value) || 0)}
              className="w-[60px] px-2 py-1 text-[13px] text-right border border-gray-300 rounded-[4px] focus:outline-none focus:border-bullogic-blue"
              min="0" max="100" step="0.5"
            />
            <span className="text-[12px] text-[#888]">%</span>
          </div>
        </div>

        {/* Annual profit comparison: before vs after scenarios */}
        <div className="grid grid-cols-3 gap-4 pt-3 border-t border-gray-100">
          <div className="text-center">
            <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#999] mb-1">Before Scenarios</div>
            <div className={`text-[22px] font-bold ${baseNetProfitPct >= floorPct ? 'text-navy' : 'text-rose'}`}>
              {formatPercent(baseNetProfitPct)}
            </div>
            <div className="text-[10px] text-[#888]">{formatCurrency(baseAnnualNOI)} NOI</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#999] mb-1">After Scenarios</div>
            <div className={`text-[22px] font-bold ${annualBelowFloor ? 'text-rose' : 'text-deep-teal'}`}>
              {formatPercent(adjustedNetProfitPct)}
            </div>
            <div className="text-[10px] text-[#888]">{formatCurrency(adjustedAnnualNOI)} NOI</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#999] mb-1">Status</div>
            {annualBelowFloor ? (
              <>
                <span className="inline-block px-3 py-1 rounded-[4px] text-[12px] font-bold text-white bg-rose">BELOW FLOOR</span>
                <div className="text-[10px] text-rose mt-1">{formatPercent(adjustedNetProfitPct)} vs {formatPercent(floorPct)} floor</div>
              </>
            ) : (
              <>
                <span className="inline-block px-3 py-1 rounded-[4px] text-[12px] font-bold text-white bg-deep-teal">ABOVE FLOOR</span>
                <div className="text-[10px] text-deep-teal mt-1">+{formatPercent(adjustedNetProfitPct - floorPct)} cushion</div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Annual Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4 text-center">
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-deep-teal block mb-1">Added Revenue</span>
          <span className="text-[20px] font-bold text-deep-teal">{formatCurrency(totalAdditionalRevenue)}</span>
        </div>
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4 text-center">
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-rose block mb-1">Added Cost</span>
          <span className="text-[20px] font-bold text-rose">{formatCurrency(totalAdditionalCost)}</span>
        </div>
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4 text-center">
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-navy block mb-1">Net Impact</span>
          <span className={`text-[20px] font-bold ${netAnnualImpact >= 0 ? 'text-deep-teal' : 'text-rose'}`}>
            {formatCurrency(netAnnualImpact)}
          </span>
        </div>
      </div>

      {/* Monthly Impact Table */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4">
        <SectionLabel>Monthly NOI Impact</SectionLabel>
        <table className="w-full text-[12px]">
          <thead>
            <tr className="bg-deep-teal text-white">
              <th className="px-3 py-2 text-left font-bold">Month</th>
              <th className="px-3 py-2 text-right font-bold">Scenario Delta</th>
              <th className="px-3 py-2 text-right font-bold">Base NOI</th>
              <th className="px-3 py-2 text-right font-bold">Adjusted NOI</th>
            </tr>
          </thead>
          <tbody>
            {monthlyImpact.map((m, i) => {
              const baseNOI = baseMonthlyNOIArray?.[i] || 0;
              const adjustedNOI = baseNOI + m.net;
              return (
                <tr key={i} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                  <td className="px-3 py-2 font-bold text-navy">{MONTH_NAMES[i]}</td>
                  <td className={`px-3 py-2 text-right ${m.net < 0 ? 'text-rose' : m.net > 0 ? 'text-deep-teal' : ''}`}>
                    {m.net !== 0 ? formatCurrency(m.net) : '--'}
                  </td>
                  <td className={`px-3 py-2 text-right ${baseNOI < 0 ? 'text-rose' : ''}`}>{formatCurrency(baseNOI)}</td>
                  <td className={`px-3 py-2 text-right font-bold ${adjustedNOI < 0 ? 'text-rose' : 'text-deep-teal'}`}>
                    {formatCurrency(adjustedNOI)}
                  </td>
                </tr>
              );
            })}
            {/* Annual total row */}
            <tr className="border-t-2 border-navy/20 bg-bone/50">
              <td className="px-3 py-2.5 font-bold text-navy">Annual Total</td>
              <td className={`px-3 py-2.5 text-right font-bold ${netAnnualImpact < 0 ? 'text-rose' : 'text-deep-teal'}`}>
                {formatCurrency(netAnnualImpact)}
              </td>
              <td className="px-3 py-2.5 text-right font-bold">{formatCurrency(baseAnnualNOI)}</td>
              <td className={`px-3 py-2.5 text-right font-bold ${adjustedAnnualNOI < 0 ? 'text-rose' : 'text-deep-teal'}`}>
                {formatCurrency(adjustedAnnualNOI)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Scenario Comparison Table */}
      {(() => {
        const activeScenarios = (scenarios || []).filter(s => s.active);
        if (activeScenarios.length === 0) return null;

        // Compute per-scenario annual cost, revenue, and net impact
        const scenarioRows = activeScenarios.map(s => {
          const monthly = calcScenarioMonthly(s);
          const annualRev = monthly.reduce((sum, m) => sum + m.revenue, 0);
          const annualCost = monthly.reduce((sum, m) => sum + m.cost, 0);
          const annualNet = monthly.reduce((sum, m) => sum + m.net, 0);
          return { id: s.id, title: s.title, type: s.type, annualRev, annualCost, annualNet };
        });

        // Sort by absolute net impact descending (biggest movers first)
        scenarioRows.sort((a, b) => Math.abs(b.annualNet) - Math.abs(a.annualNet));

        // Total net impact for percentage-of-total calculation
        const totalNetImpact = scenarioRows.reduce((s, r) => s + Math.abs(r.annualNet), 0);

        return (
          <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-4">
            <SectionLabel>Scenario Comparison</SectionLabel>
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-deep-teal text-white">
                  <th className="px-3 py-2 text-left font-bold">Scenario</th>
                  <th className="px-3 py-2 text-right font-bold">Annual Cost</th>
                  <th className="px-3 py-2 text-right font-bold">Annual Revenue</th>
                  <th className="px-3 py-2 text-right font-bold">Net Impact</th>
                  <th className="px-3 py-2 text-right font-bold">% of Total</th>
                  <th className="px-3 py-2 text-right font-bold">% of Base NOI</th>
                </tr>
              </thead>
              <tbody>
                {scenarioRows.map((row, i) => {
                  const pctOfTotal = totalNetImpact > 0 ? (Math.abs(row.annualNet) / totalNetImpact) * 100 : 0;
                  const pctOfNOI = baseAnnualNOI !== 0 ? (row.annualNet / baseAnnualNOI) * 100 : 0;
                  return (
                    <tr key={row.id} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-3 py-2 font-bold text-navy">{row.title}</td>
                      <td className={`px-3 py-2 text-right ${row.annualCost < 0 ? 'text-rose' : ''}`}>
                        {row.annualCost !== 0 ? formatCurrency(row.annualCost) : '--'}
                      </td>
                      <td className={`px-3 py-2 text-right ${row.annualRev > 0 ? 'text-deep-teal' : ''}`}>
                        {row.annualRev !== 0 ? formatCurrency(row.annualRev) : '--'}
                      </td>
                      <td className={`px-3 py-2 text-right font-bold ${row.annualNet < 0 ? 'text-rose' : 'text-deep-teal'}`}>
                        {formatCurrency(row.annualNet)}
                      </td>
                      <td className="px-3 py-2 text-right text-[#888]">
                        {pctOfTotal.toFixed(1)}%
                      </td>
                      <td className={`px-3 py-2 text-right ${pctOfNOI < 0 ? 'text-rose' : 'text-deep-teal'}`}>
                        {pctOfNOI >= 0 ? '+' : ''}{pctOfNOI.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}
    </div>
  );
}
